/**
 * @typedef {import('./index.js').Store<State, Getters>} Store
 */
/**
 * @template T
 * @typedef {import('./index.js').GetterFunction<T, State, Getters>} GetterFunction<T>
 */
/**
 * @template T
 * @template R
 * @typedef {import('./index.js').ActionFunction<T, R, State, Getters>} ActionFunction<T,R>
 */
import { capitalize } from '@utils/string-helpers'
/**
 * @module Store:Responsive
 * @description Features to handle responsivenes
 */
/**
 * Registered in load time it watches for window resizes and update height and width in {@link State}
 * @param {Store} store 
 */
export const ResizePlugin = (store) => {
  window.addEventListener('resize', () => {
    store.commit('responsive/UPDATE_RESPONSIVINESS')
    store.commit('responsive/UPDATE_BREAKPOINTS')
  })
}
// Named breakpoints based on width
export const breakpoints = {
  xs: 0,
  sm: 576,
  md: 768,
  lg: 1024,
  xl: 1280,
  xxl: 1440
}

/**
 * Creates an object with boolean values that set's 3 keys for each breakpoint:
 * - is[CapitalizedBreakpointKey] - is true if this is the current breakpoint 
 * - is[CapitalizedBreakpointKey]AndUp - is true if this is the current breakpoint or bigger
 * - is[CapitalizedBreakpointKey]AndDown - is true if this is the current breakpoint or smaller
 * @param {number} width 
 * @returns {Object<string,boolean>}
 */
const mapBreakpoints = (width) => {
  const mappedBreakpoints = {}
  Object.keys(breakpoints).forEach((bp, i) => {
    const next = Object.keys(breakpoints)[i + 1]
    mappedBreakpoints[`is${capitalize(bp)}`] = width >= breakpoints[bp] && (!next || width < breakpoints[next])
    if (i < Object.keys(breakpoints).length - 1) {
      mappedBreakpoints[`is${capitalize(bp)}AndUp`] = width >= breakpoints[bp]
    }
    if (i > 0) {
      mappedBreakpoints[`is${capitalize(bp)}AndDown`] = width <= breakpoints[bp]
    }
  })
  return mappedBreakpoints
}

export default {
  namespaced: true,
  /** Global State
   * @typedef {Object} State
   * @property {number} width - Window width, will change when resized
   * @property {number} height - Window height, will change when resized
   * @property {'landscape'|'portrait'} orientation - Orientation of the window
   * @property {Object} breakpoints - Breakpoint's defined
   * @property {Object<string,boolean>} check - Returns {@link mapBreakpoints} function with the current window width
   */
  /** @type {State} */
  state: {
    width: window.innerWidth,
    height: window.innerHeight,
    orientation: window.innerWidth > window.innerHeight ? 'landscape' : 'portrait',
    breakpoints,
    check: mapBreakpoints(window.innerWidth)
  },
  /** Global Getters
   * @typedef {Object} Getters
   * @property {GetterFunction<string>} currentBreakpoint - Returns the key of the current breakpoint
   * @property {GetterFunction<function(Object<string,number>, {round: 'up'|'down'}): string>} getValueForBestBreakpoint - Pass an object of breakpoints keys and values and it returns the value of the key that best matches the current window value, you can set if the function should round up or down
   */
  /** @type {Getters} */
  getters: {
    currentBreakpoint: (state) => {
      let currentBreakpoint = 'xs'
      const breakpointKeys = Object.keys(breakpoints)
      for (let i = 0; i < breakpointKeys.length; i++) {
        const key = breakpointKeys[i]
        const nextKey = breakpointKeys[i + 1]
        const breakpointValue = breakpoints[key]
        if (key !== 'xs') {
          const isLast = !nextKey && state.width >= breakpointValue
          const isMid = nextKey && state.width >= breakpointValue && state.width < breakpoints[nextKey]
          if (isLast || isMid) {
            currentBreakpoint = key
          }
        }
      }
      return currentBreakpoint
    },
    getValueForBestBreakpoint: (state, getters) => (object, options = { round: 'up' }) => {
      const { round = 'up' } = options
      if (!object || Array.isArray(object) || typeof object !== 'object') {
        throw new Error('first parameter needs to be an object')
      } else if (!['up', 'down'].includes(round)) {
        throw new Error('option not available. use "up" or "down"')
      }
      if (object[getters.currentBreakpoint]) {
        return object[getters.currentBreakpoint]
      } else {
        const breakpointsAvailable = Object.keys(object)
        const currentBreakpointValue = breakpoints[getters.currentBreakpoint]
        const breakpointsArray = Object.keys(breakpoints)
          .map(k => ({ key: k, value: breakpoints[k] }))
          .sort((a, b) => a.value > b.value ? 1 : -1)
        const biggerBreakpoints = breakpointsArray
          .filter(b => b.value > currentBreakpointValue)
          .map(b => b.key)
        const smallerBreakpoints = breakpointsArray
          .filter(b => b.value < currentBreakpointValue)
          .sort((a, b) => a.value > b.value ? -1 : 1)
          .map(b => b.key)
        const biggerBreakpointValue = breakpointsAvailable.find(b => biggerBreakpoints.includes(b))
        const smallerBreakpointValue = smallerBreakpoints.find(b => breakpointsAvailable.includes(b))
        const principalValue = round === 'down' ? smallerBreakpointValue : biggerBreakpointValue
        const fallbackValue = round === 'down' ? biggerBreakpointValue : smallerBreakpointValue
        if (principalValue) {
          return object[principalValue]
        } else {
          return object[fallbackValue]
        }
      }
    }
  },
  /** Mutations */
  mutations: {
    UPDATE_RESPONSIVINESS(state) {
      state.width = window.innerWidth
      state.height = window.innerHeight
      state.orientation = window.innerWidth > window.innerHeight ? 'landscape' : 'portrait'
    },
    UPDATE_BREAKPOINTS(state) {
      state.check = mapBreakpoints(state.width)
    }
  },
  /** Global Actions
   * @typedef {Object} Actions
   */
  /** @type {Actions} */
  actions: {}
}
